'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import avatecLogo from '../../assets/logos/avatec-white.png';

const VIDEO_ID = '79g3kz8XbYI';

declare global {
  interface Window {
    YT: {
      Player: new (
        el: string | HTMLElement,
        opts: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (e: { target: YTPlayer }) => void;
            onStateChange?: (e: { data: number }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  mute(): void;
  unMute(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  setPlaybackRate(rate: number): void;
  destroy(): void;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function Slide03Video() {
  const reduceMotion = useReducedMotion();
  const playerRef = useRef<YTPlayer | null>(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playerReadyRef = useRef(false);

  useEffect(() => {
    if (!speedMenuOpen) return;
    const close = () => setSpeedMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [speedMenuOpen]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      const p = playerRef.current;
      if (!p) return;
      try {
        const ct = p.getCurrentTime();
        const dur = p.getDuration();
        if (isFinite(ct)) setCurrentTime(ct);
        if (isFinite(dur) && dur > 0) setDuration(dur);
      } catch {
        // player not ready yet
      }
    }, 250);
  }, []);

  useEffect(() => {
    const initPlayer = () => {
      playerRef.current = new window.YT.Player('yt-player-div', {
        videoId: VIDEO_ID,
        playerVars: {
          autoplay: 1,
          mute: 0,
          controls: 0,
          loop: 1,
          playlist: VIDEO_ID,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          disablekb: 1,
        },
        events: {
          onReady: (e) => {
            playerReadyRef.current = true;
            e.target.setPlaybackRate(1);
            startPolling();
          },
          onStateChange: (e) => {
            if (e.data === 1) setIsPlaying(true);
            if (e.data === 2) setIsPlaying(false);
          },
        },
      });
    };

    if (window.YT?.Player) {
      initPlayer();
    } else {
      const existing = document.getElementById('yt-iframe-api-script');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'yt-iframe-api-script';
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.head.appendChild(script);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        initPlayer();
      };
    }

    return () => {
      playerReadyRef.current = false;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try { playerRef.current?.destroy(); } catch { /* ignore */ }
      playerRef.current = null;
    };
  }, [startPolling]);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  const handlePlayPause = useCallback(() => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current) return;
    if (isPlaying) {
      p.pauseVideo();
      setIsPlaying(false);
    } else {
      p.playVideo();
      setIsPlaying(true);
    }
    showControls();
  }, [isPlaying, showControls]);

  const handleSeek = useCallback((delta: number) => {
    const p = playerRef.current;
    if (!p || typeof p.getCurrentTime !== 'function') return;
    const next = Math.max(0, Math.min(duration, p.getCurrentTime() + delta));
    p.seekTo(next, true);
    setCurrentTime(next);
    showControls();
  }, [duration, showControls]);

  const handleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current) return;
    if (isMuted) {
      p.unMute();
      setIsMuted(false);
    } else {
      p.mute();
      setIsMuted(true);
    }
    showControls();
  }, [isMuted, showControls]);

  const handleSpeed = useCallback((rate: number) => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current) return;
    p.setPlaybackRate(rate);
    setPlaybackRate(rate);
    showControls();
  }, [showControls]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const seekTo = ratio * duration;
    p.seekTo(seekTo, true);
    setCurrentTime(seekTo);
    showControls();
  }, [duration, showControls]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <motion.div
      className="slide relative flex h-screen w-full overflow-hidden bg-black text-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.8, ease: 'easeOut' }}
      onMouseMove={showControls}
      onMouseLeave={() => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1000);
      }}
    >
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" style={{ zIndex: 20 }} />

      {/* YouTube player container */}
      <div className="absolute inset-0" style={{ zIndex: 1 }}>
        <div id="yt-player-div" className="h-full w-full" />
      </div>

      {/* Transparent overlay so iframe doesn't steal clicks */}
      <div
        className="absolute inset-0"
        style={{ zIndex: 10 }}
        onClick={handlePlayPause}
      />

      {/* Controls overlay */}
      <div
        className="absolute inset-x-0 bottom-0 transition-opacity duration-300 pointer-events-none"
        style={{
          zIndex: 30,
          opacity: controlsVisible ? 1 : 0,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)',
          paddingBottom: '1.5rem',
          paddingTop: '4rem',
        }}
      >
        {/* Progress bar */}
        <div
          className="px-4 sm:px-8 mb-3 pointer-events-auto"
          style={{ cursor: 'pointer' }}
        >
          <div
            className="relative h-1.5 w-full cursor-pointer rounded-full bg-white/30 group"
            onClick={handleProgressClick}
          >
            <div
              className="h-full rounded-full bg-white"
              style={{ width: `${progress}%`, transition: 'width 0.1s linear' }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 7px)` }}
            />
          </div>
        </div>

        {/* Button row */}
        <div className="flex items-center gap-3 px-4 sm:px-8 pointer-events-auto">
          {/* Rewind 10s */}
          <button
            className="flex items-center justify-center gap-1 rounded-full bg-white/10 hover:bg-white/25 transition-colors px-3"
            style={{ height: 40 }}
            onClick={(e) => { e.stopPropagation(); handleSeek(-10); }}
            title="10 saniye geri"
          >
            <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'white', userSelect: 'none' }}>10</span>
          </button>

          {/* Play / Pause */}
          <button
            className="flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors"
            style={{ width: 48, height: 48 }}
            onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
            title={isPlaying ? 'Duraklat' : 'Oynat'}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Forward 10s */}
          <button
            className="flex items-center justify-center gap-1 rounded-full bg-white/10 hover:bg-white/25 transition-colors px-3"
            style={{ height: 40 }}
            onClick={(e) => { e.stopPropagation(); handleSeek(10); }}
            title="10 saniye ileri"
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'white', userSelect: 'none' }}>10</span>
            <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
              <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" />
            </svg>
          </button>

          {/* Mute */}
          <button
            className="flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors"
            style={{ width: 40, height: 40 }}
            onClick={(e) => { e.stopPropagation(); handleMute(); }}
            title={isMuted ? 'Sesi Aç' : 'Sessiz'}
          >
            {isMuted ? (
              <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>

          {/* Time display */}
          <span className="ml-1 text-sm text-white/80 font-mono select-none">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Speed menu */}
          <div className="ml-auto relative pointer-events-auto" style={{ zIndex: 50 }}>
            {/* Popup */}
            {speedMenuOpen && (
              <div
                className="absolute bottom-12 right-0 rounded-lg overflow-hidden"
                style={{
                  background: 'rgba(28,28,28,0.97)',
                  minWidth: 140,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
                }}
              >
                <div style={{ padding: '6px 0', fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 600, letterSpacing: '0.05em', paddingLeft: 16, paddingTop: 10, paddingBottom: 6 }}>
                  Oynatma Hızı
                </div>
                {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                  <button
                    key={rate}
                    onClick={(e) => { e.stopPropagation(); handleSpeed(rate); setSpeedMenuOpen(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '9px 16px',
                      gap: 10,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: 14,
                      fontWeight: playbackRate === rate ? 700 : 400,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {playbackRate === rate && (
                        <svg viewBox="0 0 24 24" fill="white" width="16" height="16">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      )}
                    </span>
                    {rate === 1 ? 'Normal' : `${rate}x`}
                  </button>
                ))}
              </div>
            )}

            {/* Trigger button */}
            <button
              className="rounded-full transition-colors px-3 select-none"
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 700,
                color: 'white',
                background: speedMenuOpen ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
                letterSpacing: '0.03em',
              }}
              onClick={(e) => { e.stopPropagation(); setSpeedMenuOpen((o) => !o); showControls(); }}
              title="Oynatma hızı"
            >
              {playbackRate === 1 ? 'Normal' : `${playbackRate}x`}
            </button>
          </div>

          {/* Label */}
          <span className="text-lg font-semibold tracking-[0.1em] text-white/90 hidden sm:block select-none">
            Avatec Marine
          </span>
        </div>
      </div>

      {/* Static label when controls are hidden */}
      <div
        className="pointer-events-none absolute bottom-4 left-4 text-lg font-semibold tracking-[0.08em] text-white/90 sm:bottom-8 sm:left-8 sm:text-2xl sm:tracking-[0.1em] md:text-3xl md:tracking-[0.12em] transition-opacity duration-300"
        style={{ zIndex: 15, opacity: controlsVisible ? 0 : 1 }}
      >
        Avatec Marine
      </div>
    </motion.div>
  );
}
